import { Breakpoints } from '@/lib/breakpoints';
import { HEADER_HEIGHT, SITE_GUTTER } from '@/constants/layout';
import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { FontSizes, LineHeights } from '@picsart/design-system/foundation/typography';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { TextColors } from '@picsart/design-system/foundation/colors';
import { BackgroundColors } from '@picsart/design-system/foundation/colors';

const useStyles = createUseStyles(
  {
    /**
     * A step *below* the page, not above it.
     *
     * Opaque, first of all: the translucent version sampled whatever scrolled
     * under it and read as a slightly different shade from the content directly
     * beneath.
     *
     * Darker rather than lighter because of what sits on this bar. Every
     * control in it — `HeaderPricingButton`, `HeaderProjectsButton`, both
     * `HeaderAuthSection` pills — is filled `tint2`, and reads as a chip purely
     * by being lighter than the bar: 1.24:1 against `base`, and only 1.13:1
     * against `tint1`. Lifting the header to `tint2` itself erases them
     * outright. Dropping to `popover` widens the gap instead (1.27:1) and costs
     * nothing.
     *
     * It also keeps the page to one surface colour. `PresetLayout` notes why
     * that matters; here the concrete case is the preset route, where this bar
     * sits directly above the embedded miniapp — which paints `base`. A lighter
     * strip there reads as a mismatched band; a darker one reads as chrome
     * above an app, which is what it is.
     */
    header: {
      backgroundColor: BackgroundColors.popover,
    },
    inner: {
      display: 'flex',
      gap: Spacings.s12,
      alignItems: 'center',
      justifyContent: 'space-between',
      /**
       * `border-box`, so the bar is *actually* `HEADER_HEIGHT` tall.
       *
       * This repo sets no global box-sizing, so the vertical padding below was
       * being added to the declared height — the header rendered 72px while
       * everything that pins beneath it used the 56 constant, and the preset
       * page's breadcrumb row sat 16px underneath it, clipped.
       */
      boxSizing: 'border-box',
      height: HEADER_HEIGHT,
      padding: `${Spacings.s8} ${SITE_GUTTER.mobile}px`,
      [Breakpoints.SM]: { padding: `${Spacings.s8} ${SITE_GUTTER.desktop}px` },
    },
    logoLink: {
      display: 'flex',
      gap: Spacings.s8,
      alignItems: 'center',
    },
    // Rounded like the miniapp's logo — the mark is a square raster with artwork
    // to its edges, unlike the transparent SVG it replaced, so square corners
    // read as a pasted-on tile against the header.
    logoImg: { width: 32, height: 32, borderRadius: CornerRadius.r4 },
    textStack: { display: 'flex', flexDirection: 'column', justifyContent: 'space-between' },
    title: {
      fontSize: FontSizes.t5,
      fontWeight: FontWeights.bold,
      letterSpacing: '-0.025em',
      color: TextColors.base,
      lineHeight: LineHeights.t3,
    },
    subtitle: {
      fontSize: FontSizes.t3,
      fontWeight: FontWeights.normal,
      color: TextColors.base,
      lineHeight: LineHeights.t3,
    },
  },
  { name: 'header' },
);

export default useStyles;
